import React, { useState, useEffect } from 'react';
import GroupList from './GroupList';
import ChatInterface from './ChatInterface';
import { a2aMessageService } from '../../a2a/services/a2aMessageService.js';
import { getTenantIdFromUser } from '../../a2a/tenantHelper.js';
import './GroupMessagesModule.css';

const GroupMessagesModule = ({ currentUser, onBack, onTabChange }) => {
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [groups, setGroups] = useState([]);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [isGroupListCollapsed, setIsGroupListCollapsed] = useState(false);

  // Get tenant_id from currentUser (multi-tenant support)
  const tenantId = getTenantIdFromUser(currentUser);

  useEffect(() => {
    // Use the passed currentUser
    if (currentUser) {
      // Map the currentUser to a format that works with our system
      const userId = currentUser?.id || currentUser?.user?.id;
      setUser({
        id: userId,
        email: currentUser?.email || currentUser?.user?.email || 'user@myclass.edu.hk',
        role: currentUser?.role || currentUser?.user?.role || 'teacher'
      });
    }
  }, [currentUser]);

  useEffect(() => {
    if (user) {
      loadUserGroups();
    }
  }, [user]);

  useEffect(() => {
    if (selectedGroup) {
      loadGroupMessages(selectedGroup.id);
      setupRealTimeSubscription(selectedGroup.id);
    }
  }, [selectedGroup]);

  const loadUserGroups = async () => {
    try {
      if (!user?.id) {
        console.warn('Cannot load groups: user ID is missing');
        setLoading(false);
        return;
      }

      const response = await a2aMessageService.getMessageGroups({
        tenantId: tenantId,
        userId: user.id
      });

      if (!response.success) {
        console.error('Error loading user groups:', response.error);
        setGroups([]);
        setLoading(false);
        return;
      }

      if (!response.data || response.data.length === 0) {
        setGroups([]);
        setLoading(false);
        return;
      }

      setGroups(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error loading groups:', error);
      setGroups([]);
      setLoading(false);
    }
  };

  const loadGroupMessages = async (groupId) => {
    try {
      const response = await a2aMessageService.getMessages({
        tenantId: tenantId,
        groupId: groupId,
        limit: 100
      });

      if (!response.success) {
        console.error('Error loading messages:', response.error);
        return;
      }

      setMessages(response.data || []);
    } catch (error) {
      console.error('Error loading messages:', error);
    }
  };

  const setupRealTimeSubscription = (groupId) => {
    // TODO: A2A doesn't support real-time subscriptions yet
    // For now, we'll poll for new messages every 5 seconds
    const pollInterval = setInterval(() => {
      loadGroupMessages(groupId);
    }, 5000);

    return () => {
      clearInterval(pollInterval);
    };
  };

  const handleNewMessage = (newMessage) => {
    setMessages(prev => [...prev, newMessage]);
  };

  const handleStatusUpdate = (statusUpdate) => {
    setMessages(prev => 
      prev.map(msg => 
        msg.id === statusUpdate.message_id 
          ? { ...msg, status: statusUpdate.status }
          : msg
      )
    );
  };

  const handleGroupSelect = (group) => {
    setSelectedGroup(group);
  };

  const handleToggleGroupList = () => {
    setIsGroupListCollapsed(!isGroupListCollapsed);
  };

  const handleSendMessage = async (content, messageType = 'text', attachments = []) => {
    console.log('handleSendMessage called with:', { content, messageType, attachments });
    console.log('Current state:', { selectedGroup, user, loading });
    
    if (!selectedGroup || !user) {
      console.error('Cannot send message: missing group or user');
      console.log('selectedGroup:', selectedGroup);
      console.log('user:', user);
      return;
    }

    try {
      console.log('Sending message:', { content, messageType, groupId: selectedGroup.id, userId: user.id });
      
      const response = await a2aMessageService.sendMessage({
        tenantId: tenantId,
        groupId: selectedGroup.id,
        senderId: user.id,
        content,
        messageType: messageType,
        attachments: attachments.length > 0 ? attachments : []
      });

      if (!response.success) {
        console.error('Error sending message:', response.error);
        throw new Error(response.error);
      }

      console.log('Message sent successfully:', response.data);
      
      // Add message immediately to the UI for better UX
      handleNewMessage(response.data);
      
    } catch (error) {
      console.error('Error sending message:', error);
      // You could add a toast notification here
    }
  };

  if (loading) {
    return (
      <div className="group-messages-module loading">
        <div className="loading-spinner">載入中...</div>
      </div>
    );
  }

  return (
    <div className="group-messages-module">
      <div className="group-messages-header">
        <h2>小組訊息</h2>
        <p>Group Messages</p>
      </div>
      
      <div className="group-messages-content">
        <GroupList 
          groups={groups}
          selectedGroup={selectedGroup}
          onGroupSelect={handleGroupSelect}
          currentUser={user}
          isCollapsed={isGroupListCollapsed}
          onToggleCollapse={handleToggleGroupList}
        />
        
        {selectedGroup ? (
          <ChatInterface 
            group={selectedGroup}
            messages={messages}
            onSendMessage={handleSendMessage}
            currentUser={user}
          />
        ) : (
          <div className="no-group-selected">
            <div className="no-group-content">
              <h3>選擇群組開始聊天</h3>
              <p>Select a group to start chatting</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default GroupMessagesModule;
